import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/auth';
import Button from '../../components/ui/Button';

const ReauthenticatePage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      setSuccess(true);
      
      // Redirection après 2 secondes
      setTimeout(() => {
        navigate('/app');
      }, 2000);

    } catch (err: any) {
      console.error('Erreur lors de la réauthentification:', err);
      
      // Messages d'erreur personnalisés
      let errorMessage = 'Une erreur est survenue lors de la connexion.';
      
      if (err.message?.includes('Invalid login credentials')) {
        errorMessage = 'Email ou mot de passe incorrect.';
      } else if (err.message?.includes('Email not confirmed')) {
        errorMessage = 'Votre email n\'a pas encore été confirmé. Vérifiez votre boîte mail.';
      } else if (err.message?.includes('Too many requests')) {
        errorMessage = 'Trop de tentatives de connexion. Veuillez patienter avant de réessayer.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Réauthentification réussie !
              </h2>
              <p className="text-gray-600">
                Vous avez été réauthentifié avec succès. Vous allez être redirigé vers le dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-pink-100 rounded-full">
            <Shield className="h-8 w-8 text-pink-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          Réauthentification requise
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Pour des raisons de sécurité, veuillez confirmer votre identité
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Adresse email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 sm:text-sm transition-colors"
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 sm:text-sm transition-colors"
                  placeholder="Votre mot de passe"
                />
              </div>
            </div>

            <div>
              <Button
                type="submit"
                fullWidth
                disabled={isLoading || !email || !password}
                className="py-3"
              >
                {isLoading ? 'Vérification en cours...' : 'Confirmer l\'identité'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/app')}
              className="text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              Retour au dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReauthenticatePage;